import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { classes } from 'record-it-components/utils/utils';
import { WorkTimeEntryBreak } from 'record-it-time-common/entity/entities/WorkTimeEntry/WorkTimeEntryDto';
import {
  CreateTimeEntryBreakInline,
  CreateTimeEntryBreakInlineProps
} from '../CreateTimeEntryBreakInline/CreateTimeEntryBreakInline';
import {
  WorkTimeEntryBreakListItem,
  WorkTimeEntryBreakListItemProps
} from '../WorkTimeEntryBreakListItem/WorkTimeEntryBreakListItem';
import styles from './EditWorkTimeEntryBreaksWidget.module.css';

export type EditWorkTimeEntryBreaksWidgetProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  /**
   * Start date a new break is initially set to.
   */
  newBreakInitialStartDate?: Date;
  /**
   * End date a new break is initially set to.
   */
  newBreakInitialEndDate?: Date;
  workTimeEntryBreaks: Array<WorkTimeEntryBreak>;
  onWorkTimeEntryBreaksModified: (breaks: Array<WorkTimeEntryBreak>) => void;
};

export const EditWorkTimeEntryBreaksWidget: FunctionComponent<EditWorkTimeEntryBreaksWidgetProps> =
  React.memo((props) => {
    const {
      workTimeEntryBreaks,
      onWorkTimeEntryBreaksModified,
      className,
      newBreakInitialStartDate,
      newBreakInitialEndDate,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();

    const handleCreateTimeEntry: CreateTimeEntryBreakInlineProps['onCreate'] =
      useEventCallback((toCreate) => {
        const lastIndex =
          workTimeEntryBreaks[workTimeEntryBreaks.length - 1]?.index ?? -1;
        onWorkTimeEntryBreaksModified([
          ...workTimeEntryBreaks,
          {
            index: lastIndex + 1,
            startDate: toCreate.startDate,
            endDate: toCreate.endDate
          }
        ]);
      });

    const handleDeleteTimeEntry: WorkTimeEntryBreakListItemProps['onDelete'] =
      useEventCallback((timeEntryBreak) => {
        const newBreaks = workTimeEntryBreaks
          .filter((b) => b !== timeEntryBreak)
          .map((b, index) => {
            if (b.index === index) {
              return b;
            }

            return { ...b, index };
          });

        onWorkTimeEntryBreaksModified(newBreaks);
      });

    return (
      <div
        {...remainingProps}
        className={classes(className, styles.container)}
      >
        <div className={styles.title}>
          {t('components.timeTracking.EditWorkTimeEntryBreaksWidget.title')}
        </div>

        <div className={styles.list}>
          {workTimeEntryBreaks.map((b) => (
            <WorkTimeEntryBreakListItem
              key={b.index}
              workTimeEntryBreak={b}
              onDelete={handleDeleteTimeEntry}
              data-testid="EditWorkTimeEntryBreaksWidget.BreakListItem"
            ></WorkTimeEntryBreakListItem>
          ))}
        </div>

        <CreateTimeEntryBreakInline
          onCreate={handleCreateTimeEntry}
          initialStartDate={newBreakInitialStartDate}
          initialEndDate={newBreakInitialEndDate}
          data-testid="EditWorkTimeEntryBreaksWidget.CreateTimeEntryBreakInline"
        ></CreateTimeEntryBreakInline>
      </div>
    );
  });
